import { create } from 'zustand'
import type { NormalizedMenu } from '../menu/normalize'
import { productCount, subcategoryCount } from '../menu/normalize'

/**
 * Máquina de estado de la navegación 2D.
 *
 * El eje Y recorre subcategorías, el X recorre productos dentro de la
 * subcategoría actual. Es un reducer explícito y no una librería de máquinas de
 * estado porque el conjunto de transiciones cabe en esta pantalla, y tenerlo
 * aquí hace que las reglas de borde —los topes, el salto entre categorías— sean
 * legibles de un vistazo.
 */

export type Phase = 'explorer' | 'immersive' | 'map' | 'cart' | 'search'

export type NavState = {
  phase: Phase
  /** Coordenadas: categoría, subcategoría, producto. */
  c: number
  s: number
  p: number
  /** Sentido de la última transición. Alimenta la dirección de la animación. */
  direction: -1 | 0 | 1
  /** Eje del último movimiento, para animar en el plano correcto. */
  axis: 'x' | 'y' | null
  /**
   * Marca temporal del último intento de sobrepasar el final de una categoría.
   *
   * Cambiar de categoría con un solo gesto es demasiado fácil de disparar sin
   * querer: el comensal llega al último plato, insiste un poco y se encuentra
   * en otra sección sin saber cómo. Exigimos dos gestos seguidos.
   */
  overscrollAt: number
}

export type NavActions = {
  attach(menu: NormalizedMenu): void
  nextProduct(): void
  prevProduct(): void
  nextSubcategory(): void
  prevSubcategory(): void
  jump(c: number, s: number, p?: number): void
  enterCategory(c: number): void
  setPhase(phase: Phase): void
  backToExplorer(): void
}

const DOUBLE_INTENT_MS = 800

/** Resultado de una transición que no se pudo completar: el borde se resiste. */
export type EdgeFeedback = { at: number; axis: 'x' | 'y'; direction: -1 | 1 }

let menuRef: NormalizedMenu | null = null

/** Notificación de rebote para la UI: vibración corta y animación elástica. */
let edgeListener: ((feedback: EdgeFeedback) => void) | null = null

export function onEdge(listener: (feedback: EdgeFeedback) => void) {
  edgeListener = listener
  return () => {
    edgeListener = null
  }
}

function edge(axis: 'x' | 'y', direction: -1 | 1) {
  edgeListener?.({ at: Date.now(), axis, direction })
}

export const useNavigator = create<NavState & NavActions>((set, get) => ({
  phase: 'explorer',
  c: 0,
  s: 0,
  p: 0,
  direction: 0,
  axis: null,
  overscrollAt: 0,

  attach(menu) {
    menuRef = menu
  },

  nextProduct() {
    const { c, s, p } = get()
    if (!menuRef) return

    // Sin bucle infinito: llegar al final y reaparecer al principio desorienta,
    // porque el comensal pierde la noción de cuántos platos ha visto.
    if (p + 1 >= productCount(menuRef, c, s)) {
      edge('x', 1)
      return
    }
    set({ p: p + 1, direction: 1, axis: 'x' })
  },

  prevProduct() {
    const { p } = get()
    if (p === 0) {
      edge('x', -1)
      return
    }
    set({ p: p - 1, direction: -1, axis: 'x' })
  },

  nextSubcategory() {
    const { c, s, overscrollAt } = get()
    if (!menuRef) return

    if (s + 1 < subcategoryCount(menuRef, c)) {
      // Volver al primer producto: entrar a mitad de una subcategoría nueva
      // haría que los puntos indicadores mintieran sobre la posición.
      set({ s: s + 1, p: 0, direction: 1, axis: 'y', overscrollAt: 0 })
      return
    }

    // Estamos en la última subcategoría: solo un segundo gesto cambia de categoría.
    const now = Date.now()
    if (now - overscrollAt < DOUBLE_INTENT_MS && c + 1 < menuRef.categories.length) {
      set({ c: c + 1, s: 0, p: 0, direction: 1, axis: 'y', overscrollAt: 0 })
      return
    }

    edge('y', 1)
    set({ overscrollAt: now })
  },

  prevSubcategory() {
    const { c, s, overscrollAt } = get()
    if (!menuRef) return

    if (s > 0) {
      set({ s: s - 1, p: 0, direction: -1, axis: 'y', overscrollAt: 0 })
      return
    }

    const now = Date.now()
    if (now - overscrollAt < DOUBLE_INTENT_MS && c > 0) {
      const previousCategory = c - 1
      // Al retroceder de categoría se entra por su ÚLTIMA subcategoría: es
      // donde estaría el comensal si hubiera llegado navegando hacia delante.
      const last = Math.max(0, subcategoryCount(menuRef, previousCategory) - 1)
      set({ c: previousCategory, s: last, p: 0, direction: -1, axis: 'y', overscrollAt: 0 })
      return
    }

    edge('y', -1)
    set({ overscrollAt: now })
  },

  jump(c, s, p = 0) {
    if (!menuRef) return

    // Un salto desde el mapa o la búsqueda puede apuntar a coordenadas que ya no
    // existen si la carta se refrescó entre medias.
    const safeC = clamp(c, 0, menuRef.categories.length - 1)
    const safeS = clamp(s, 0, Math.max(0, subcategoryCount(menuRef, safeC) - 1))
    const safeP = clamp(p, 0, Math.max(0, productCount(menuRef, safeC, safeS) - 1))

    const current = get()
    // La dirección se deduce comparando la posición absoluta, para que la
    // animación del salto vaya hacia donde el comensal espera.
    const before = ordinalOf(current.c, current.s, current.p)
    const after = ordinalOf(safeC, safeS, safeP)

    set({
      phase: 'immersive',
      c: safeC,
      s: safeS,
      p: safeP,
      direction: after >= before ? 1 : -1,
      axis: 'y',
      overscrollAt: 0,
    })
  },

  enterCategory(c) {
    get().jump(c, 0, 0)
  },

  setPhase(phase) {
    set({ phase })
  },

  backToExplorer() {
    set({ phase: 'explorer', direction: 0, axis: null, overscrollAt: 0 })
  },
}))

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min
  return Math.min(Math.max(value, min), max)
}

/** Posición absoluta aproximada, solo para decidir el sentido de la animación. */
function ordinalOf(c: number, s: number, p: number): number {
  return c * 10_000 + s * 100 + p
}

// ── Selectores ─────────────────────────────────────────────────────────────
// Suscribirse al store entero haría que cada componente se repintara en cada
// gesto. Estos selectores acotan la reactividad a lo que cada uno usa.

export const selectCoords = (state: NavState) => `${state.c}:${state.s}:${state.p}`
export const selectPhase = (state: NavState) => state.phase
